import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AnimationsService } from 'src/app/services/animations.service';
import { TefService } from 'src/app/services/tef/tef.service';
import { API } from 'src/app/services/api';
import { environment } from 'src/environments/environment';

/** Formas de pagamento disponíveis na composição */
export enum FormaPgtoEnum {
  CartaoCredito = 1,
  CartaoDebito   = 2,
  BoletoBancario = 3,
  PixTef         = 11,
}

/** Dados de entrada do dialog — passados pelo painel financeiro */
export interface DadosMultiplosPagamentos {
  valorTotal: number;
  matriculaId: number;
  cursoId: number;
  planoPagamentoCursoId: number;
  usuarioLogadoId: number;
  unidadeLogadoId?: number;  // S-01: para validação IDOR
  tipoPagamentoInicial: number;   // US-003 v2: tipo da aba de origem (1=CC, 2=Débito, 11=PixTef)
  maxParcelasCC: number;          // US-003 v2: máximo de parcelas para CC
  // Contexto de mudança de plano (opcionais — ausentes = fluxo de contratação normal)
  isMudancaPlano?: boolean;
  novoPlanoPagamentoId?: number;
  parcelasParaCancelar?: number[];
}

/** Chave localStorage para persistência de sessão (RF-11) */
const LS_KEY_PREFIX = 'composicao_';
const LS_TTL_HORAS = 24;

@Component({
  selector: 'app-mpp-multiplos-pagamentos',
  templateUrl: './mpp-multiplos-pagamentos.component.html',
  styleUrls: ['./mpp-multiplos-pagamentos.component.scss']
})
export class MppMultiplosPagamentosComponent implements OnInit, OnDestroy {

  // ── State ──────────────────────────────────────────────────────────────────
  composicaoId: number = null;
  composicao: any = null;
  isLoading: boolean = false;
  erroMsg: string = null;
  etapa: 'inicializando' | 'pronto' | 'tef_pendente' | 'finalizado' = 'inicializando';

  /** RF-08: confirmar cancelamento quando há TEFs aprovados */
  confirmarCancelamento: boolean = false;

  // ── RF-02: Planos de parcelamento para cartão crédito ──────────────────────
  planosParcelamento: any[] = [];
  isLoadingPlanos: boolean = false;
  parcelasDisponiveis: any[] = [];

  // ── RF-12: Configuração de feature flags por unidade ───────────────────────
  pixTefHabilitado: boolean = true;
  pixMensagemModoManual: string = null;

  // ── Formulário ─────────────────────────────────────────────────────────────
  form: FormGroup;
  readonly FormaPgto = FormaPgtoEnum;

  formasPgtoDisponiveis = [
    { label: 'Cartão de Crédito', value: FormaPgtoEnum.CartaoCredito },
    { label: 'Cartão de Débito',  value: FormaPgtoEnum.CartaoDebito },
    { label: 'Pix via TEF',       value: FormaPgtoEnum.PixTef },
    // US-004 v2: Boleto não é opcão manual — gerado automaticamente se houver saldo
  ];

  formasPgtoFiltradas: any[] = [];

  // US-004 v2: Sem Price table — valor cobrado = valor abatido (sem juros)

  constructor(
    public dialogRef: MatDialogRef<MppMultiplosPagamentosComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DadosMultiplosPagamentos,
    private fb: FormBuilder,
    private http: HttpClient,
    private animationsService: AnimationsService,
    private tefService: TefService,
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.buildForm();
    this.carregarConfiguracaoUnidade();
    this.iniciarComposicao();
  }

  ngOnDestroy(): void {}

  // ── Init ───────────────────────────────────────────────────────────────────
  private buildForm(): void {
    // US-004 v2: Pré-selecionar forma de pagamento inicial
    const formaPagamentoInicial = this.data?.tipoPagamentoInicial ?? FormaPgtoEnum.CartaoCredito;
    const maxParcelasCC = this.data?.maxParcelasCC ?? 12;

    // Gerar parcelas disponíveis baseado em maxParcelasCC
    this.gerarParcelasDisponiveis();

    this.form = this.fb.group({
      // US-004 v2: 1º pagamento é travado na forma/parcelas do plano selecionado (regra espelhada no backend
      // em ComposicaoPagamentoService.AdicionarPagamentoTefAsync — itensAprovados == 0). Libera a partir do 2º.
      formaPagamento: [{ value: formaPagamentoInicial, disabled: true }, Validators.required],
      planoParcelamento: [null],   // RF-02: obrigatório somente para CC
      valor:            [null, [Validators.required, Validators.min(0.01)]],
      dataVencimento:   [null],
      parcelas:         [{ value: maxParcelasCC, disabled: true }],  // Iniciar com máximo de parcelas
    });
  }

  /** US-004 v2: trava formaPagamento/parcelas no 1º pagamento, libera a partir do 2º (espelha regra do backend) */
  private atualizarTravaCampos(itens: any[]): void {
    const temItensAprovados = (itens ?? []).some(i => i.status === 1 || i.status === 'Aprovado');
    const formaCtrl = this.form.get('formaPagamento');
    const parcelasCtrl = this.form.get('parcelas');
    if (temItensAprovados) {
      formaCtrl.enable({ emitEvent: false });
      parcelasCtrl.enable({ emitEvent: false });
    } else {
      formaCtrl.disable({ emitEvent: false });
      parcelasCtrl.disable({ emitEvent: false });
    }
  }

  private gerarParcelasDisponiveis(): void {
    const maxParcelas = this.data?.maxParcelasCC ?? 12;
    this.parcelasDisponiveis = Array.from({ length: maxParcelas }, (_, i) => ({
      label: i === 0 ? 'À vista' : `${i + 1}x`,
      value: i + 1,
    }));
  }

  private carregarConfiguracaoUnidade(): void {
    // RF-12: Carregar configuração de feature flags por unidade
    let params = new HttpParams();
    if (this.data.unidadeLogadoId) {
      params = params.set('unidadeId', this.data.unidadeLogadoId.toString());
    }
    const url = `${environment.baseUrl}${API.composicaoPagamento.configuracao}`;
    this.http.get<any>(url, { params }).subscribe({
      next: (config) => {
        this.pixTefHabilitado = config.pixTefHabilitado === true;
        this.pixMensagemModoManual = config.pixMensagemModoManual;
        this.atualizarFormasPgtoFiltradas();
      },
      error: () => {
        // Fallback: assumir habilitado se erro ao carregar
        this.pixTefHabilitado = true;
        this.atualizarFormasPgtoFiltradas();
      }
    });
  }

  private atualizarFormasPgtoFiltradas(): void {
    // RF-12: Filtrar "Pix via TEF" se desabilitado na unidade
    this.formasPgtoFiltradas = this.formasPgtoDisponiveis.filter(fp =>
      fp.value !== FormaPgtoEnum.PixTef || this.pixTefHabilitado
    );
  }

  private iniciarComposicao(): void {
    this.isLoading = true;
    this.erroMsg = null;

    // RF-11: verificar sessão salva no localStorage
    const sessaoSalva = this.carregarSessaoLocalStorage();
    if (sessaoSalva) {
      // Revalidar estado no servidor
      this.validarSessaoSalva(sessaoSalva);
      return;
    }

    const url = `${environment.baseUrl}${API.composicaoPagamento.iniciar}/${this.data.matriculaId}/Iniciar`;
    const body = {
      planoPagamentoCursoId: this.data.planoPagamentoCursoId,
      valorTotal: this.data.valorTotal,
      usuarioLogadoId: this.data.usuarioLogadoId,
      unidadeLogadoId: this.data.unidadeLogadoId ?? null,   // S-01: IDOR
      tipoPagamentoInicial: this.data?.tipoPagamentoInicial ?? FormaPgtoEnum.CartaoCredito,
      maxParcelasCC: this.data?.maxParcelasCC ?? 12,
    };
    this.http.post<any>(url, body).subscribe({
      next: (resp) => {
        this.composicaoId = resp.composicaoId;
        this.composicao = resp;
        this.etapa = 'pronto';
        this.form.patchValue({ valor: resp.saldoRestante });
        this.atualizarTravaCampos(resp.itens);

        this.isLoading = false;
      },
      error: (err) => {
        this.erroMsg = err?.error?.mensagem ?? 'Erro ao iniciar composição';
        this.isLoading = false;
      }
    });
  }

  // ── RF-11: localStorage ─────────────────────────────────────────────────────
  private get lsKey(): string { return `${LS_KEY_PREFIX}${this.data.matriculaId}`; }

  private salvarSessaoLocalStorage(): void {
    if (!this.composicaoId) return;
    const payload = {
      composicaoId: this.composicaoId,
      matriculaId: this.data.matriculaId,
      valorTotal: this.data.valorTotal,
      savedAt: Date.now(),
    };
    try { localStorage.setItem(this.lsKey, JSON.stringify(payload)); } catch (_) {}
  }

  private carregarSessaoLocalStorage(): any {
    try {
      const raw = localStorage.getItem(this.lsKey);
      if (!raw) return null;
      const payload = JSON.parse(raw);
      const idadeHoras = (Date.now() - payload.savedAt) / 3600000;
      if (idadeHoras > LS_TTL_HORAS) { this.limparSessaoLocalStorage(); return null; }
      return payload;
    } catch (_) { return null; }
  }

  private limparSessaoLocalStorage(): void {
    try { localStorage.removeItem(this.lsKey); } catch (_) {}
  }

  private validarSessaoSalva(sessao: any): void {
    const url = `${environment.baseUrl}${API.composicaoPagamento.consultar}/${sessao.composicaoId}`;
    this.http.get<any>(url).subscribe({
      next: (resp) => {
        const mesmoPlano = resp?.planoPagamentoCursoId === this.data.planoPagamentoCursoId;
        if (resp?.status === 'EmAndamento' && mesmoPlano) {
          this.composicaoId = sessao.composicaoId;
          this.composicao = resp;
          this.etapa = 'pronto';
          this.form.patchValue({ valor: resp.saldoRestante });
          this.atualizarTravaCampos(resp.itens);
          this.animationsService.showSuccessSnackBar('Sessão anterior recuperada. Continue o pagamento.');
        } else {
          // Composição finalizada/cancelada em outro contexto, OU usuário trocou de plano —
          // não recuperar sessão obsoleta; deixar o backend decidir (substituir ou bloquear) via POST /Iniciar
          this.limparSessaoLocalStorage();
          this.iniciarComposicao();
          return;
        }
        this.isLoading = false;
      },
      error: () => {
        this.limparSessaoLocalStorage();
        this.iniciarComposicao();
      }
    });
  }

  // ── Getters ─────────────────────────────────────────────────────────────────
  get formaPagamentoSelecionada(): number {
    return this.form.get('formaPagamento')?.value;
  }

  get isBoleto(): boolean {
    return this.formaPagamentoSelecionada === FormaPgtoEnum.BoletoBancario;
  }

  get isCartaoCredito(): boolean {
    return this.formaPagamentoSelecionada === FormaPgtoEnum.CartaoCredito;
  }

  get podeFinalizar(): boolean {
    return this.composicao?.podeFinalizar === true;
  }

  get saldoRestante(): number {
    return this.composicao?.saldoRestante ?? this.data.valorTotal;
  }

  get progressoPct(): number {
    const total = this.composicao?.valorTotal ?? this.data.valorTotal;
    const pago = this.composicao?.valorPago ?? 0;
    if (!total || total === 0) return 0;
    return Math.min(100, Math.round((pago / total) * 100));
  }

  // ── RF-02: Ao mudar forma de pagamento ─────────────────────────────────────
  onFormaPagamentoChange(): void {
    const dataCtrl = this.form.get('dataVencimento');
    const planoCtrl = this.form.get('planoParcelamento');

    if (this.isBoleto) {
      dataCtrl.setValidators(Validators.required);
      planoCtrl.clearValidators();
      this.form.patchValue({ valor: this.saldoRestante, planoParcelamento: null, parcelas: 1 });
    } else if (this.isCartaoCredito) {
      dataCtrl.clearValidators();
      planoCtrl.clearValidators();
      this.form.patchValue({ parcelas: 1 });
    } else {
      dataCtrl.clearValidators();
      planoCtrl.clearValidators();
      this.planosParcelamento = [];
      this.form.patchValue({ planoParcelamento: null, parcelas: 1 });
    }

    dataCtrl.updateValueAndValidity();
    planoCtrl.updateValueAndValidity();
  }

  // RF-02: Carregar planos do backend
  private carregarPlanosParcelamento(): void {
    if (!this.data.cursoId) return;
    this.isLoadingPlanos = true;
    const formaPagamento = this.form.get('formaPagamento')?.value ?? FormaPgtoEnum.CartaoCredito;
    const params = new HttpParams()
      .set('formaPagamento', formaPagamento.toString())
      .set('cursoId', this.data.cursoId.toString())
      .set('unidadeId', (this.data.unidadeLogadoId ?? 1).toString());
    const url = `${environment.baseUrl}${API.planoPagamento.buscarPlanoPagamento}`;
    this.http.get<any[]>(url, { params }).subscribe({
      next: (planos) => {
        this.planosParcelamento = planos ?? [];
        this.isLoadingPlanos = false;
      },
      error: () => {
        this.isLoadingPlanos = false;
        // Fallback: parcelas 1-12 hard-coded
        this.planosParcelamento = Array.from({ length: 12 }, (_, i) => ({
          parcelas: i + 1,
          descricao: i === 0 ? 'À vista' : `${i + 1}x`,
        }));
      }
    });
  }

  // RF-02: Ao selecionar plano, atualiza o campo parcelas
  onPlanoParcelamentoChange(): void {
    const plano = this.form.get('planoParcelamento')?.value;
    if (plano?.parcelas) {
      this.form.patchValue({ parcelas: plano.parcelas });
    }
  }


  // ── Processar TEF ──────────────────────────────────────────────────────────
  processarTef(): void {
    if (this.form.invalid) return;

    const valor: number = this.form.get('valor').value;
    const fp: number    = this.formaPagamentoSelecionada;
    const tipoTransacao = fp === FormaPgtoEnum.PixTef ? 2 : (fp === FormaPgtoEnum.CartaoCredito ? 1 : 0);

    let valorTotal: number;
    const strValor = valor.toString();
    if (strValor.indexOf('.') > -1) {
      const parts = strValor.split('.');
      valorTotal = parseInt(`${parts[0]}${parts[1].slice(0, 2).padEnd(2, '0')}`);
    } else {
      valorTotal = parseInt(`${strValor}00`);
    }

    const payload = {
      valorTotal,
      tipoTransacao,
      matriculaId: this.data.matriculaId,
      composicaoId: this.composicaoId,
      usuarioLogadoId: this.data.usuarioLogadoId
    };
    this.isLoading = true;
    this.etapa = 'tef_pendente';

    this.tefService.transacaoTef(payload).subscribe({
      next: (val) => {
        this.isLoading = false;
        // TefService converte falha de rede em next {status:'error'} via ErrorHandlerService —
        // tratar como falha para não registrar pagamento fantasma com dados TEF nulos
        if (val?.codigo > 0 || val?.status === 'error') {
          this.etapa = 'pronto';
          const msg = val?.status === 'error'
            ? 'Falha na comunicação com TEF'
            : 'TEF não aprovado. Código: ' + val.codigo;
          this.animationsService.showErrorSnackBar(msg);
          return;
        }
        // TEF já registrou o pagamento na composição via backend — apenas atualizar a tela
        this.etapa = 'pronto';
        this.recarregarComposicao();
        this.form.patchValue({ valor: this.saldoRestante });
        this.animationsService.showSuccessSnackBar('Pagamento adicionado com sucesso');
        this.salvarSessaoLocalStorage();
      },
      error: () => {
        this.isLoading = false;
        this.etapa = 'pronto';
        this.animationsService.showErrorSnackBar('Falha na comunicação com TEF');
      }
    });
  }

  // ── Processar Boleto ───────────────────────────────────────────────────────
  processarBoleto(): void {
    if (this.form.invalid) return;
    const dataVenc: Date = this.form.get('dataVencimento').value;
    const body = {
      formaPagamento: FormaPgtoEnum.BoletoBancario,
      valor: this.saldoRestante,
      dataVencimento: dataVenc ? dataVenc.toISOString() : null,
      usuarioLogadoId: this.data.usuarioLogadoId,
    };
    this.isLoading = true;
    this.postAdicionarPagamento(body);
  }

  private postAdicionarPagamento(body: any): void {
    const url = `${environment.baseUrl}${API.composicaoPagamento.adicionarPagamento}/${this.composicaoId}/AdicionarPagamento`;
    this.http.post<any>(url, body).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.etapa = 'pronto';
        this.recarregarComposicao();
        this.form.patchValue({ valor: this.saldoRestante });
        this.animationsService.showSuccessSnackBar('Pagamento adicionado com sucesso');
        // RF-11: persistir sessão após cada pagamento aprovado
        this.salvarSessaoLocalStorage();
      },
      error: (err) => {
        this.isLoading = false;
        this.etapa = 'pronto';
        const mensagem: string = err?.error?.mensagem ?? 'Erro ao adicionar pagamento';
        this.animationsService.showErrorSnackBar(mensagem);
        // Composição cancelada/finalizada (ex: concorrência entre abas) → limpar sessão e reiniciar
        if (/cancelad|finalizad/i.test(mensagem)) {
          this.limparSessaoLocalStorage();
          this.composicaoId = null;
          this.iniciarComposicao();
        }
      }
    });
  }

  private recarregarComposicao(): void {
    const url = `${environment.baseUrl}${API.composicaoPagamento.consultar}/${this.composicaoId}`;
    this.http.get<any>(url).subscribe({
      next: (resp) => {
        this.composicao = resp;
        this.form.patchValue({ valor: resp.saldoRestante });
        this.atualizarTravaCampos(resp.itens);
      },
      error: () => {}
    });
  }

  // ── Finalizar ──────────────────────────────────────────────────────────────
  finalizar(): void {
    if (!this.podeFinalizar) return;
    this.isLoading = true;
    let params = new HttpParams().set('usuarioLogadoId', String(this.data.usuarioLogadoId));
    if (this.data.unidadeLogadoId != null) params = params.set('unidadeLogadoId', String(this.data.unidadeLogadoId));
    const url = `${environment.baseUrl}${API.composicaoPagamento.finalizar}/${this.composicaoId}/Finalizar`;
    this.http.post<any>(url, null, { params }).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.etapa = 'finalizado';
        this.limparSessaoLocalStorage();  // RF-11
        const msg = resp?.boletosGerados > 0
          ? `Composição finalizada! Boleto de R$ ${Number(resp.valorBoleto).toFixed(2)} gerado, vencimento em 10 dias.`
          : 'Composição finalizada com sucesso!';
        this.animationsService.showSuccessSnackBar(msg);
        this.dialogRef.close({ finalizado: true, composicaoId: this.composicaoId, resp });
      },
      error: (err) => {
        this.isLoading = false;
        this.animationsService.showErrorSnackBar(err?.error?.mensagem ?? 'Erro ao finalizar');
      }
    });
  }

  /** Fecha o dialog sem cancelar a composição — ela permanece EmAndamento, recuperável via RF-11 */
  fecharDialog(): void {
    this.dialogRef.close(null);
  }

  // ── Cancelar (RF-08) ───────────────────────────────────────────────────────
  /** RF-08: Se há TEFs aprovados, exibe confirmação primeiro */
  cancelar(): void {
    if (!this.composicaoId) {
      this.limparSessaoLocalStorage();
      this.dialogRef.close(null);
      return;
    }
    const temTefAprovado = this.composicao?.itens?.some(
      (i: any) => i.formaPagamento !== 3 && (i.status === 'Aprovado' || i.status === 0)
    );
    if (temTefAprovado) {
      this.confirmarCancelamento = true;
      return;
    }
    this.executarCancelamento();
  }

  /** RF-08: Chamado após confirmação do usuário */
  executarCancelamento(): void {
    this.confirmarCancelamento = false;
    this.isLoading = true;
    let params = new HttpParams().set('usuarioLogadoId', String(this.data.usuarioLogadoId));
    if (this.data.unidadeLogadoId != null) params = params.set('unidadeLogadoId', String(this.data.unidadeLogadoId));
    const url = `${environment.baseUrl}${API.composicaoPagamento.cancelar}/${this.composicaoId}/Cancelar`;
    this.http.post<any>(url, null, { params }).subscribe({
      next: (resp) => {
        this.isLoading = false;
        this.limparSessaoLocalStorage();  // RF-11
        if (resp?.requerEstornoManual) {
          this.animationsService.showErrorSnackBar(
            `Atenção: R$ ${resp.valorTefAprovado?.toFixed(2)} em TEFs aprovados requerem estorno manual na maquininha.`
          );
        }
        this.dialogRef.close(null);
      },
      error: () => {
        this.isLoading = false;
        this.limparSessaoLocalStorage();
        this.dialogRef.close(null);
      }
    });
  }

  // ── Máscara monetária ──────────────────────────────────────────────────────
  onValorInput(event: any): void {
    let valor = event.target.value.replace(/\D/g, '');
    if (valor) {
      const numerico = parseInt(valor, 10) / 100;
      this.form.patchValue({ valor: numerico.toFixed(2) }, { emitEvent: false });
      event.target.value = numerico.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  onValorBlur(): void {
    const valor = this.form.get('valor')?.value;
    if (valor) {
      const numerico = parseFloat(valor);
      // Manter o valor numérico no form mas com máscara no input
      this.form.patchValue({ valor: numerico }, { emitEvent: false });
      const inputElement = (document.querySelector('input[formControlName="valor"]') as HTMLInputElement);
      if (inputElement) {
        inputElement.value = numerico.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
  }

  // ── Labels ─────────────────────────────────────────────────────────────────
  labelFormaPagamento(fp: number): string {
    return this.formasPgtoDisponiveis.find(f => f.value === fp)?.label ?? `Forma ${fp}`;
  }

  labelStatusItem(status: string): string {
    const map = { Aprovado: 'Aprovado', Gerado: 'Gerado', Negado: 'Negado', Cancelado: 'Cancelado' };
    return map[status] ?? status;
  }
}
